import { MotionBox, MotionBoxProps } from '@/components/common/Motion';
import { Box } from '@chakra-ui/react';
import styleguide from '@common/styleguide';
import { motion, Variants } from 'framer-motion';

export interface PlentySpinnerProps extends MotionBoxProps {
    showAnimation?: boolean;
    onAnimationComplete?: () => void;
    message?: JSX.Element;
    dynamicLines?: string[];
    size?: number;
    onDynamicLinesComplete?: () => void;
}

export const plentySpinnerVariants: Variants = {
    enter: {
        opacity: 0,
    },
    center: {
        opacity: 1,
        transition: {
            duration: 0.5,
        },
    },

    initial: { pathLength: 0 },
    loading: {
        pathLength: 1,
        transition: {
            repeatType: 'reverse',
            repeat: 1,
            type: 'easeInOut',
            duration: 2,
        },
    },
    loadingInfinite: {
        pathLength: 1,
        transition: {
            repeatType: 'reverse',
            repeat: Infinity,
            type: 'easeInOut',
            duration: 2,
        },
    },
};

export default function PlentySpinner({
    showAnimation = true,
    size = 40,
    onAnimationComplete,
    message,
    dynamicLines,
    onDynamicLinesComplete,
    ...props
}: PlentySpinnerProps) {
    const svgProps = showAnimation
        ? {
              initial: 'initial',
              animate: onAnimationComplete ? 'loading' : 'loadingInfinite',

              onAnimationComplete: onAnimationComplete,
          }
        : {
              pathLength: 1,
          };

    return (
        <MotionBox
            h="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            variants={plentySpinnerVariants}
            initial="enter"
            animate="center"
            {...props}
        >
            <MotionBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
            >
                <motion.svg
                    width={size}
                    height={size}
                    viewBox="0 0 77 46"
                    {...svgProps}
                >
                    <motion.path
                        d="M 27.72 27.411 L 19.463 16.583 L 3.064 42.938 L 21.757 42.938 L 49.28 3.063 L 73.936 42.938 L 38.5 42.938 L 32.479 34.516"
                        fill="transparent"
                        strokeWidth={4.13}
                        strokeLinejoin="round"
                        stroke={styleguide.semantic.textDark}
                        variants={plentySpinnerVariants}
                    />
                </motion.svg>
                {message && (
                    <MotionBox
                        mt={6}
                        color="blackAlpha.700"
                        fontSize="lg"
                        fontWeight={600}
                        textAlign="center"
                    >
                        {message}
                    </MotionBox>
                )}
                {dynamicLines && (
                    <Box pos="relative" mt={1} w={350} h={50}>
                        {dynamicLines.map((dl, index) => (
                            <MotionBox
                                key={dl}
                                color="blackAlpha.700"
                                fontSize="lg"
                                fontWeight={600}
                                textAlign="center"
                                pos="absolute"
                                w="full"
                                top={0}
                                initial={{ top: 0, opacity: 0, y: 100 }}
                                animate={{
                                    top: 0,
                                    y: [100, 0, 0, 0, 0, -20],
                                    opacity: [0, 1, 1, 1, 1, 0],
                                    transition: {
                                        delay: index * 2,
                                        duration: 2,
                                        ease: 'easeOut',
                                    },
                                }}
                                onAnimationComplete={
                                    index === dynamicLines.length - 1
                                        ? onDynamicLinesComplete
                                        : undefined
                                }
                            >
                                {dl}
                            </MotionBox>
                        ))}
                    </Box>
                )}
            </MotionBox>
        </MotionBox>
    );
}
